/**
 * Renders a Mermaid diagram from source text to SVG.
 * Theme-aware (dark/light) and gracefully falls back to raw source on parse errors.
 */

import React, { useEffect, useId, useState } from 'react';
import mermaid from 'mermaid';
import { useMantineColorScheme } from '@mantine/core';
import { Alert, Code, Box, Skeleton } from '@mantine/core';
import { LoadingState } from './LoadingState';

export interface MermaidDiagramProps {
    chart: string;
}

let mermaidInitialized = false;

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
    const { colorScheme } = useMantineColorScheme();
    const uniqueId = useId();
    const elementId = `mermaid-${uniqueId.replace(/:/g, '')}`;
    const [svg, setSvg] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const theme = colorScheme === 'dark' ? 'dark' : 'default';

        if (!mermaidInitialized) {
            mermaid.initialize({
                startOnLoad: false,
                theme,
                securityLevel: 'strict',
                fontFamily: 'inherit',
            });
            mermaidInitialized = true;
        } else {
            mermaid.initialize({ theme });
        }

        let cancelled = false;

        (async () => {
            try {
                const { svg: renderedSvg } = await mermaid.render(elementId, chart);
                if (!cancelled) {
                    setSvg(renderedSvg);
                    setError(null);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setSvg(null);
                    setError(err?.message || 'Failed to render Mermaid diagram');
                }
                // mermaid.render may insert a temp element on failure; clean it up
                const staleEl = document.getElementById('d' + elementId);
                if (staleEl) staleEl.remove();
            }
        })();

        return () => { cancelled = true; };
    }, [chart, colorScheme, elementId]);

    if (error) {
        return (
            <Box my="xs">
                <Alert color="red" title="Mermaid diagram error" variant="light">
                    {error}
                </Alert>
                <Code block mt="xs" fz="sm">
                    {chart}
                </Code>
            </Box>
        );
    }

    if (!svg) {
        return (
            <Box my="xs" p="md" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
                <LoadingState variant="centered" label="Rendering diagram..." size="xs" />
                <Skeleton height={120} radius="sm" />
            </Box>
        );
    }

    return (
        <Box
            my="xs"
            style={{ overflow: 'auto' }}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};
